import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Button, Chip, Stack } from '@mui/material'
import { formatDateToFEFormatDateFns } from 'utils/date'
import T from 'T'
import { WORK_ORDER_STATUS_FILTER_MENU_OPTIONS } from '../../work-orders/filters/work-order-status-filter/settings'

const ActiveFilters = ({
  selectedRange,
  selectedRoutes,
  selectedServices,
  selectedWorkOrderStatuses,
  onRemoveRoute,
  onRemoveService,
  onRemoveStatus,
  onClearDateRange,
  onResetAll,
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false)

  const selectedRangeText = useMemo(() => {
    if (selectedRange && selectedRange[0] && selectedRange[1]) {
      const startDateFormatted = formatDateToFEFormatDateFns(selectedRange[0])
      const endDateFormatted = formatDateToFEFormatDateFns(selectedRange[1])
      return `${startDateFormatted} - ${endDateFormatted}`
    }
    if (selectedRange && selectedRange[0]) {
      return formatDateToFEFormatDateFns(selectedRange[0])
    }
    return T.ALL_WORK_ORDERS_NO_DATE_FILTER
  }, [selectedRange])

  const activeFiltersCount = useMemo(() => {
    const routesCount = selectedRoutes?.length || 0
    const servicesCount = selectedServices?.length || 0
    const hasStatusChanges = selectedWorkOrderStatuses?.length !== WORK_ORDER_STATUS_FILTER_MENU_OPTIONS.length
    const statusesCount = hasStatusChanges ? selectedWorkOrderStatuses?.length || 0 : 0
    const hasDateRange = selectedRange && selectedRange[0] ? 1 : 0
    return routesCount + servicesCount + statusesCount + hasDateRange
  }, [selectedRoutes, selectedServices, selectedWorkOrderStatuses, selectedRange])

  const handleToggleFiltersExpanded = useCallback(() => {
    setIsFiltersExpanded(prev => !prev)
  }, [])

  if (activeFiltersCount === 0) {
    return null
  }

  return (
    <Box mt={2} p={2} pb={0}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight={600} sx={{ color: 'text.secondary' }}>
          {T.ACTIVE_FILTERS}:
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {activeFiltersCount}
        </Typography>
        <Button variant="text" size="small" sx={{ color: 'text.secondary' }} onClick={handleToggleFiltersExpanded}>
          {isFiltersExpanded ? T.HIDE : T.SHOW}
        </Button>
        <Button variant="text" size="small" sx={{ color: 'text.secondary' }} onClick={onResetAll}>
          {T.RESET_ALL}
        </Button>
      </Box>
      {isFiltersExpanded && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
          {selectedRange && selectedRange[0] && (
            <Chip key="date-range" label={`${T.DATE}: ${selectedRangeText}`} onDelete={onClearDateRange} size="small" color="info" />
          )}
          {selectedRoutes?.map(route => (
            <Chip key={`route-${route.id}`} label={`${T.ROUTE}: ${route.value}`} onDelete={() => onRemoveRoute(route)} size="small" />
          ))}
          {selectedServices?.map(service => (
            <Chip
              key={`service-${service.id}`}
              label={`${T.SERVICE}: ${service.value}`}
              onDelete={() => onRemoveService(service)}
              size="small"
            />
          ))}
          {selectedWorkOrderStatuses?.length !== WORK_ORDER_STATUS_FILTER_MENU_OPTIONS.length &&
            selectedWorkOrderStatuses?.map(status => (
              <Chip
                key={`status-${status.id}`}
                label={`${T.STATUS}: ${status.value}`}
                onDelete={() => onRemoveStatus(status)}
                size="small"
              />
            ))}
        </Stack>
      )}
    </Box>
  )
}

ActiveFilters.propTypes = {
  selectedRange: PropTypes.array,
  selectedRoutes: PropTypes.array,
  selectedServices: PropTypes.array,
  selectedWorkOrderStatuses: PropTypes.array,
  onRemoveRoute: PropTypes.func.isRequired,
  onRemoveService: PropTypes.func.isRequired,
  onRemoveStatus: PropTypes.func.isRequired,
  onClearDateRange: PropTypes.func.isRequired,
  onResetAll: PropTypes.func.isRequired,
}

export default ActiveFilters
